import { Link } from "../lib/route";
import { Phone, Mail, MapPin, Flame, Instagram, Twitter, Facebook, Linkedin, Youtube } from "lucide-react";
import { SITE, SERVICES, COPY, FOOTER } from "../lib/_data";

const SOCIAL_ICON = {
  instagram: Instagram,
  twitter: Twitter,
  x: Twitter,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
};

const DEFAULT_QUICK_LINKS = [
  { label: "About Us", link: "/about" },
  { label: "Book a Consultation", link: "/consultations" },
  { label: "Reviews", link: "/reviews" },
  { label: "FAQ", link: "/faq" },
  { label: "Contact", link: "/contact" },
];

export default function Footer() {
  const tagline = FOOTER.tagline || COPY.brand.footerBlurb ||
    "A locally-owned home services company built on clear communication, honest pricing, and workmanship you can rely on.";
  const socials = (Array.isArray(FOOTER.socials) ? FOOTER.socials : [])
    .filter((s) => s && s.platform && SOCIAL_ICON[s.platform]);
  const quickLinks = (Array.isArray(FOOTER.quick_links) && FOOTER.quick_links.length > 0)
    ? FOOTER.quick_links
    : DEFAULT_QUICK_LINKS;
  const showContact = FOOTER.show_contact !== false;
  const showAddress = FOOTER.show_address !== false;
  const copyrightText = FOOTER.copyright ||
    `© ${new Date().getFullYear()} ${SITE.brandFull || SITE.brand}. All rights reserved.`;
  const newsletterEnabled = !!FOOTER.newsletter_enabled;

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#2c2a29] text-[#f9f6f0] pt-20 pb-28 md:pb-16"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-2 mb-5">
            <span className="w-10 h-10 rounded-full bg-[#c85a3c] flex items-center justify-center">
              <Flame className="w-5 h-5" strokeWidth={2.4} />
            </span>
            <span className="font-display text-2xl font-bold" data-testid="footer-brand">
              {SITE.brand}
            </span>
          </div>
          <p className="text-sm text-[#c9c2b4] leading-relaxed max-w-xs" data-testid="footer-tagline-text">
            {tagline}
          </p>
          {showContact && (
            <div className="mt-6 flex flex-col gap-3 text-sm">
              {SITE.phone && (
                <a
                  href={SITE.phoneHref}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 hover:text-[#c85a3c] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#c85a3c]" /> {SITE.phone}
                </a>
              )}
              {SITE.email && (
                <a
                  href={SITE.emailHref}
                  data-testid="footer-email"
                  className="flex items-center gap-3 hover:text-[#c85a3c] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#c85a3c]" /> {SITE.email}
                </a>
              )}
              {showAddress && SITE.address && (
                <div className="flex items-center gap-3" data-testid="footer-address">
                  <MapPin className="w-4 h-4 text-[#c85a3c]" /> {SITE.address}
                </div>
              )}
            </div>
          )}
          {socials.length > 0 && (
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {socials.map((s, i) => {
                const Icon = SOCIAL_ICON[s.platform];
                return (
                  <a
                    key={`${s.platform}-${i}`}
                    href={s.url || "#"}
                    aria-label={s.platform}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full bg-[#3d3a38] hover:bg-[#c85a3c] transition-colors flex items-center justify-center"
                    data-testid={`footer-social-${s.platform}`}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h4 className="font-display text-lg font-bold mb-5">Services</h4>
          <ul className="space-y-3 text-sm text-[#c9c2b4]">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/services"
                  data-testid={`footer-service-${s.slug}`}
                  className="hover:text-[#c85a3c] transition-colors"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg font-bold mb-5">Quick Links</h4>
          <ul className="space-y-3 text-sm text-[#c9c2b4]" data-testid="footer-quick-links">
            {quickLinks.map((l, i) => (
              <li key={`${l.label}-${i}`}>
                <Link
                  to={l.link || "/"}
                  data-testid={`footer-quick-link-${i}`}
                  className="hover:text-[#c85a3c]"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {newsletterEnabled ? (
          <div data-testid="footer-newsletter">
            <h4 className="font-display text-lg font-bold mb-3">
              {FOOTER.newsletter_heading || "Stay in the loop"}
            </h4>
            <p className="text-sm text-[#c9c2b4] mb-4 leading-relaxed">
              {FOOTER.newsletter_subtitle || "Get updates, deals, and helpful tips."}
            </p>
            <form
              className="flex flex-col gap-2"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                placeholder="your@email.com"
                className="w-full rounded-md bg-[#3d3a38] text-[#f9f6f0] placeholder-[#8a8580] px-3 py-2 text-sm border border-[#4a4644] focus:border-[#c85a3c] focus:outline-none"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                className="w-full bg-[#c85a3c] hover:bg-[#a64b32] text-[#fffdf9] py-2 rounded-md font-bold text-sm"
                data-testid="footer-newsletter-submit"
              >
                {FOOTER.newsletter_cta_text || "Subscribe"}
              </button>
            </form>
          </div>
        ) : (
          <div>
            <h4 className="font-display text-lg font-bold mb-5">Service Areas</h4>
            <ul className="grid grid-cols-2 gap-2 text-sm text-[#c9c2b4]">
              {(SITE.serviceAreas || []).slice(0, 10).map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="footer-check-availability"
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#c85a3c] hover:underline"
            >
              Check availability →
            </Link>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 mt-14 pt-8 border-t border-[#3d3a38] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#8a8580]">
        <div data-testid="footer-copyright">{copyrightText}</div>
        <div className="flex gap-6">
          <span className="hover:text-[#f9f6f0] cursor-pointer">Privacy Policy</span>
          <span className="hover:text-[#f9f6f0] cursor-pointer">Terms of Service</span>
        </div>
      </div>
    </footer>
  );
}
